import { ChevronRightIcon } from 'lucide-react';

import { DimeButton } from '../DimeButton.js';
import { cn } from '../lib/utils.js';

interface TreeExpandToggleProps {
  canExpand: boolean;
  isExpanded: boolean;
  onClick: () => void;
}

/**
 * Lives inside the indent column's own cell rather than a separate column — DimeNestedTable's
 * expand column can't indent, and indentation is the entire visual grammar of a tree table.
 *
 * A leaf (`canExpand` false) renders a same-width spacer instead of nothing, so every row's value
 * in this column stays flush regardless of depth or expandability — only the button, byte-identical
 * to DimeNestedTableToggle on the tabIndex/aria-label decisions, is new here.
 */
export function TreeExpandToggle({
  canExpand,
  isExpanded,
  onClick,
}: Readonly<TreeExpandToggleProps>) {
  if (!canExpand) {
    // size-8 matches DimeButton's size="icon" footprint below, so a leaf's value lines up with an
    // expandable sibling's value one chevron-width to the right of it.
    return <span aria-hidden="true" className="inline-block size-8" />;
  }

  return (
    <DimeButton
      variant="ghost"
      size="icon"
      // Excluded from the tab sequence so the roving-tabindex row is the single stop per row; the
      // row's own onKeyDown handles Enter/Space/ArrowRight/ArrowLeft in its place.
      tabIndex={-1}
      aria-label={`${isExpanded ? 'Collapse' : 'Expand'} row`}
      onClick={(event) => {
        // The row itself may have its own onClick for selection (onSelectRow) — the chevron must
        // not also select on every toggle click.
        event.stopPropagation();
        onClick();
      }}
    >
      <ChevronRightIcon className={cn('transition-transform', isExpanded && 'rotate-90')} />
    </DimeButton>
  );
}
